/**
 * stateNames.ts — USPS two-letter abbreviation → full US state name (T-380)
 *
 * WHY: the state choropleth tier labels its hover tooltip with whatever the feature
 * carries, and both transports (the state_choropleth MV and the PMTiles pyramid) carry
 * `state_abbrev` only — "PA", never "Pennsylvania". Ricardo: a non-US reader cannot be
 * expected to decode 51 two-letter codes, and the tooltip has the room.
 *
 * WHY CLIENT-SIDE: state names are static reference data, not project data. Carrying the
 * name through the MV + the tile pyramid instead would mean a migration, a re-tile and a
 * production window to change a label — cost with no benefit, since the mapping never
 * changes. The trade-off is a second copy of the state table, so its drift is asserted
 * against the canonical one (pipeline/config/states.py) in stateNames.test.ts.
 *
 * Entries and spellings are copied verbatim from that table (50 states + DC).
 */

export const US_STATE_NAMES: Readonly<Record<string, string>> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
}

/**
 * Full state name for a USPS abbreviation, falling back to the input unchanged.
 *
 * The fallback is deliberate: an unknown code (a territory we have not onboarded, a
 * malformed tile property) still shows the operator SOMETHING real rather than a blank
 * line or an invented "Unknown" — the same degrade-honestly rule the rest of the app
 * follows. Empty in, empty out; the caller decides whether to render the line at all.
 */
export function stateLabel(abbrev: string | null | undefined): string {
  const raw = (abbrev ?? '').trim()
  if (raw === '') return ''
  return US_STATE_NAMES[raw.toUpperCase()] ?? raw
}
